import React, { useState, useEffect } from 'react';
import { Button, Input, Select, Textarea } from '../../components/common';

/**
 * One field, edited where it is read.
 *
 * The page used to have a single Edit button that swapped the whole tab for a
 * form and sent every field back on save — including the DLR YAML, which meant
 * correcting a typo in a website URL rewrote the app's permissions. The update
 * endpoint applies `if 'field' in data`, so a payload naming one field changes
 * one field; this component is that contract made visible.
 *
 * `onSave` receives a patch object and is expected to throw on failure, which
 * keeps the row in edit mode with the draft intact rather than silently
 * discarding what was typed.
 */
function InlineField({
  label,
  name,
  value,
  render,
  type = 'text',
  options,
  placeholder,
  hint,
  onSave,
  editable = true,
  toPatch,
  parse,
  staged = false,
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? '');
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (!editing) setDraft(value ?? ''); }, [value, editing]);

  const dirty = String(draft ?? '') !== String(value ?? '');

  const commit = async () => {
    if (!dirty) { setEditing(false); return; }
    setSaving(true);
    try {
      const next = parse ? parse(draft) : draft;
      await onSave(toPatch ? toPatch(next) : { [name]: next }, label);
      setEditing(false);
    } catch {
      // onSave surfaces the error; stay open so the draft is not lost.
    }
    setSaving(false);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Escape') { setDraft(value ?? ''); setEditing(false); }
    // Enter commits single-line fields; a textarea needs it for newlines.
    if (e.key === 'Enter' && type !== 'textarea') { e.preventDefault(); commit(); }
  };

  if (!editing) {
    const shown = render ? render(value) : (value || null);
    if (!editable) {
      return (
        <div className="appfield">
          <span className="appfield__label">{label}</span>
          <div className="appfield__value">
            {shown ?? <span className="appfield__empty">Not set</span>}
          </div>
        </div>
      );
    }
    // The whole row is the control. Edit used to appear only on hover, which
    // meant nothing on screen said these rows could be changed — you had to
    // already know, or find it by accident. A link inside the value would also
    // fight the row for the click, so the row is a div with a button in it
    // rather than a button wrapping everything.
    return (
      <div
        className="appfield appfield--editable"
        onClick={e => { if (!e.target.closest('a')) setEditing(true); }}
      >
        <span className="appfield__label">{label}</span>
        <div className="appfield__value">
          {shown ?? <span className="appfield__empty">Not set</span>}
          {staged && (
            <span className="appfield__staged" title="A change to this field is staged but not committed">
              staged
            </span>
          )}
        </div>
        <button
          className="appfield__edit"
          onClick={e => { e.stopPropagation(); setEditing(true); }}
          aria-label={`Edit ${label}`}
        >
          Edit
        </button>
      </div>
    );
  }

  return (
    <div className="appfield appfield--editing">
      <span className="appfield__label">{label}</span>
      <div className="appfield__value">
        {type === 'select' ? (
          <Select autoFocus value={draft} onChange={e => setDraft(e.target.value)} onKeyDown={onKeyDown}>
            {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </Select>
        ) : type === 'textarea' ? (
          <Textarea
            autoFocus value={draft} rows={3} placeholder={placeholder}
            onChange={e => setDraft(e.target.value)} onKeyDown={onKeyDown}
          />
        ) : (
          <Input
            autoFocus type={type} value={draft} placeholder={placeholder}
            onChange={e => setDraft(e.target.value)} onKeyDown={onKeyDown}
          />
        )}
        {hint && <p className="appfield__hint">{hint}</p>}
      </div>
      <div className="appfield__actions">
        <Button size="sm" variant="secondary" onClick={() => { setDraft(value ?? ''); setEditing(false); }}>
          Cancel
        </Button>
        <Button size="sm" onClick={commit} disabled={saving || !dirty}>
          {saving ? 'Staging…' : 'Stage Changes'}
        </Button>
      </div>
    </div>
  );
}

/** A value you copy rather than change — an identifier the platform assigned. */
export function ReadOnlyField({ label, value, mono, onCopy, copyLabel, secret }) {
  // A secret is masked until asked for: it is on screen for screenshots and
  // screen shares far more often than anyone needs to read it.
  const [shown, setShown] = useState(false);
  if (!value) return null;
  const masked = secret && !shown ? `${'•'.repeat(20)}${String(value).slice(-4)}` : value;
  return (
    <div className="appfield appfield--readonly">
      <span className="appfield__label">{label}</span>
      <div className="appfield__value">
        {mono ? <code className="appfield__code">{masked}</code> : <span>{masked}</span>}
      </div>
      {(onCopy || secret) && (
        <span className="appfield__acts">
          {secret && (
            <button className="appfield__edit" onClick={() => setShown((v) => !v)}>
              {shown ? 'Hide' : 'Reveal'}
            </button>
          )}
          {onCopy && (
            <button className="appfield__edit" onClick={() => onCopy(value, copyLabel || label)}>
              Copy
            </button>
          )}
        </span>
      )}
    </div>
  );
}

export default InlineField;
